import { useEffect, useState } from 'react';
import { Stack, SelectChangeEvent } from '@mui/material';
import { useAccounts } from '@/controllers/gridManager/accountsManager/accountManager';
import { getDataInfoMF } from '@/controllers/gridManager/followerTransManager/followerTransManager';
import CheckboxElement from '@/views/elements/checkBox/CheckBox';
import ButtonElement from '@/views/elements/button/ButtonElement';
import classNames from 'classnames/bind';
import styles from './RegisterMasterModalStep.module.scss';
import ViewNumberFormat from '@/views/elements/Inputs/ViewNumberFormat';
import { useGetCosConfig } from '@/controllers/gridManager/appManager/appManager';
import { useTheme } from '@mui/system';
import TypographyElement from '@/views/elements/typography/TypographyElement';
import AccountSelector from '@/views/components/componentsUI/AccountSelector';
import { t } from 'i18next';

const cx = classNames.bind(styles);

enum cdcontentText {
  '00' = 'Thường',
  '09' = 'Thường'
}

type RegisterMasterStep1Props = {
  handleClose?: () => void,
  handleNextStep?: (data: {} | null) => void
}

const RegisterMasterModalStep1 = ({ handleClose, handleNextStep }: RegisterMasterStep1Props) => {
  const theme = useTheme()
  const {
    accounts,
  }: {
    accounts: {
      acctno: string;
      cdcontent: keyof typeof cdcontentText;
      custodycd: string;
    }[];
  } = useAccounts();
  const { cosConfig } = useGetCosConfig()
  const { minnetasset } = cosConfig
  const [agree, setAgree] = useState(false);
  const [account, setAccount] = useState(accounts?.[0]?.acctno || "");

  const [dataInfo, setDataInfo] = useState<{
    baldefovd: string;
    monthtraded: string;
    netasset: string;
    minmonthtraded: string;
    custodycd: string;
    minnetasset: string;
  }>();

  useEffect(() => {
    loadDataInfo(account)
  }, [account])

  const toggleAgree = () => {
    setAgree(!agree)
  }

  const loadDataInfo = async (account: string) => {
    const response = await getDataInfoMF(account)
    if (response.isSuccess) setDataInfo({ ...response.data, minnetasset })
  }

  const handleAccountChange = (event: SelectChangeEvent<typeof account>) => {
    setAccount(event.target.value);
  }

  return (
    <>
      <Stack
        direction={"column"}
        spacing='5px'
        alignItems="center"
        marginBottom='15px !important'
        sx={{
          "& > *": { width: "100%" },
        }}
      >
        <TypographyElement lineHeight='21px'>
          {t('copytrade.registerMaster.linkaccountNumber')} <span style={{ color: "red" }}>*</span>
        </TypographyElement>
        <AccountSelector
          currentAccountId={account}
          handleChangeAccount={handleAccountChange}
        />
      </Stack>
      <Stack direction={"row"} fontWeight={400} marginBottom='15px !important' lineHeight='21px' justifyContent={"space-between"}>
        <TypographyElement ColorElement='secondary'>{t('copytrade.registerMaster.custodycd')}</TypographyElement>
        <TypographyElement>{dataInfo?.custodycd}</TypographyElement>
      </Stack>
      <Stack direction={"row"} fontWeight={400} marginBottom='15px !important' lineHeight='21px' justifyContent={"space-between"}>
        <TypographyElement ColorElement='secondary'>{t('copytrade.registerMaster.availableBalance')}</TypographyElement>
        <ViewNumberFormat color="primary" align='right' value={dataInfo?.baldefovd} />
      </Stack>
      <Stack direction={"row"} fontWeight={400} marginBottom='15px !important' lineHeight='21px' justifyContent={"space-between"}>
        <TypographyElement ColorElement='secondary'>{t('copytrade.registerMaster.netasset')}</TypographyElement>
        <ViewNumberFormat color="primary" align='right' value={dataInfo?.netasset} />
      </Stack>
      <Stack direction={"row"} fontWeight={400} marginBottom='15px !important' lineHeight='21px' justifyContent={"space-between"}>
        <TypographyElement ColorElement='secondary'>{t('copytrade.registerMaster.minnetasset')} {"(>=)"}</TypographyElement>
        <ViewNumberFormat color="primary" align='right' value={dataInfo?.minnetasset} />
      </Stack>
      <Stack direction={"row"} fontWeight={400} marginBottom='15px !important' lineHeight='21px' justifyContent={"space-between"}>
        <TypographyElement ColorElement='secondary'>{t('copytrade.registerMaster.monthtraded')}</TypographyElement>
        <TypographyElement>{dataInfo?.monthtraded}</TypographyElement>
      </Stack>
      <Stack direction={"row"} fontWeight={400} marginBottom='15px !important' lineHeight='21px' justifyContent={"space-between"}>
        <TypographyElement ColorElement='secondary'>{t('copytrade.registerMaster.minmonthtraded')} {"(>=)"}</TypographyElement>
        <TypographyElement>{dataInfo?.minmonthtraded}</TypographyElement>
      </Stack>
      <Stack direction={"row"} fontWeight={400} alignItems='flex-start' borderRadius='4px' marginBottom='20px !important' paddingTop='2px' justifyContent={"space-between"}
        sx={{ border: `1px solid ${theme.palette.border}` }}>
        <CheckboxElement
          checked={agree}
          onChange={toggleAgree}
          inputProps={{ "aria-label": "controlled" }}
        />
        <TypographyElement padding='10px' marginLeft='-8px' marginTop='-2px'>
          {t('copytrade.registerMaster.commit.span1')}
          <span style={{ color: theme.palette.text.brand }}> {t('copytrade.registerMaster.commit.span2')} </span>
          {t('copytrade.registerMaster.commit.span3')}
          <span style={{ color: theme.palette.text.brand }}> {t('copytrade.registerMaster.commit.span4')}</span>
        </TypographyElement>
      </Stack>
      <Stack direction={"row"} spacing={2} justifyContent={"center"}>
        <ButtonElement
          className={cx("footer-btn")}
          variant="contained"
          onClick={handleClose}
          customType='Secondary'
          sx={{ width: '100%', fontSize: '14px', fontWeight: '500', lineHeight: '21px' }}
        >
          {t('copytrade.common.close')}
        </ButtonElement>
        <ButtonElement
          className={cx("footer-btn")}
          variant="contained"
          onClick={() => !!handleNextStep && handleNextStep(dataInfo || null)}
          disabled={agree === false}
          customType='Primary'
          sx={{ width: '100%', fontSize: '14px', fontWeight: '500', lineHeight: '21px' }}
        >
          {t('copytrade.common.register')}
        </ButtonElement>
      </Stack>
    </>
  );
};

export default RegisterMasterModalStep1;